/**
 * Starting-tempo rules for repertoire pieces (MUS-29).
 *
 * Every piece opens at a fraction of its goal tempo so the first read-through is
 * approachable. The fraction is an *initial default only*: once the user moves the
 * tempo, that value is persisted as `last_tempo` and always wins on reload/re-entry.
 */

/** Fraction of a piece's goal tempo a user starts at before any manual change. */
export const DEFAULT_TEMPO_FRACTION_OF_GOAL = 0.7;

/** Fallback goal tempo for pieces with no BPM recorded. */
export const FALLBACK_GOAL_TEMPO_BPM = 72;

/** Matches the slider bounds in TempoContext; the default must stay playable. */
const MIN_TEMPO_BPM = 40;

const isUsableTempo = (tempo: number | null | undefined): tempo is number =>
  typeof tempo === 'number' && Number.isFinite(tempo) && tempo > 0;

/** The goal (performance) tempo of a piece, falling back when its BPM is missing. */
export const resolveGoalTempo = (pieceBpm: number | null | undefined): number =>
  isUsableTempo(pieceBpm) ? pieceBpm : FALLBACK_GOAL_TEMPO_BPM;

/** The default starting tempo for a piece the user has never adjusted. */
export const defaultStartingTempo = (goalTempo: number): number =>
  Math.max(MIN_TEMPO_BPM, Math.round(goalTempo * DEFAULT_TEMPO_FRACTION_OF_GOAL));

/**
 * The tempo a piece should open at:
 *
 * - the user's last manually-set tempo when one exists;
 * - otherwise the goal tempo, if the piece already has a progress record — a
 *   piece played before this change landed carries progress but no stored tempo,
 *   and must not be knocked down to 70% retroactively;
 * - otherwise {@link defaultStartingTempo}, for a piece never attempted at all.
 */
export const resolveInitialRepertoireTempo = (
  lastTempo: number | null | undefined,
  goalTempo: number | null | undefined,
  hasProgressRecord: boolean,
): number => {
  if (isUsableTempo(lastTempo)) return lastTempo;
  const goal = resolveGoalTempo(goalTempo);
  return hasProgressRecord ? goal : defaultStartingTempo(goal);
};
